/** @file The profile a puppet renders with: its profile plus the puppet's overrides (z scales, tilePx). */
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { tilePxOf } from '@zilo/pose3d-format/profile/tilePxOf';

/**
 * A profile whose z scales are resolved: `zScaleRotate` default 1, `zScaleView` default `1 / cos(camera.pitch)`.
 * `tilePx` (22.1) is the puppet override, else the profile's own; when neither states it, it stays absent (bundles of
 * the built-in profiles do not change) and `tilePxOf` gives the default `[ppu, ppu, ppu]`.
 */
export type EffectiveProfile = Profile & { zScaleRotate: number; zScaleView: number };

/**
 * `profiles[puppet.profile]` with `puppet.profileOverrides` applied (17.7; `tilePx` CR-007, 22.1, 22.5);
 * throws `PROFILE_UNKNOWN` at `/profile`.
 */
export function effectiveProfile(
  puppet: Pick<Puppet, 'profile' | 'profileOverrides'>, profiles: Readonly<Record<string, Profile>>,
): EffectiveProfile {
  const profile = Object.hasOwn(profiles, puppet.profile) ? profiles[puppet.profile] : undefined;
  if (profile === undefined) {
    return throwError(ERROR_CODES.PROFILE_UNKNOWN, { profile: puppet.profile }, { path: '/profile', hint: Object.keys(profiles).join(', ') });
  }
  const overrides = puppet.profileOverrides ?? {};
  const tilePx = overrides.tilePx ?? profile.tilePx;
  return {
    ...profile,
    zScaleRotate: overrides.zScaleRotate ?? profile.zScaleRotate ?? 1,
    zScaleView: overrides.zScaleView ?? profile.zScaleView ?? 1 / Math.cos((profile.camera.pitch * Math.PI) / 180),
    ...(tilePx === undefined ? {} : { tilePx: tilePxOf({ pixelsPerUnit: profile.pixelsPerUnit, tilePx }) }),
  };
}
